// Разобранный лист протокола и пункт (003: data-model.md, ingestion.version_items; FR-003 – FR-012).
// Хозяин всех полей пункта — Excel (принцип I). Один и тот же контракт у разбора без базы
// (показ 0, demo:sheet) и у фоновой задачи разбора в сервисе.
import { z } from '../kernel/zod.ts';
import { DateSchema } from '../kernel/common.ts';
import { EvidenceSchema } from './evidence.ts';
import { ItemStructureSchema, SheetStructureSchema, StructureRunSchema } from './structure.ts';

/** item — пункт, subitem — подпункт («–» в начале текста или номер вида «7.1.»). */
export const itemKinds = ['item', 'subitem'] as const;
export type ItemKind = (typeof itemKinds)[number];
export const ItemKindSchema = z.enum(itemKinds);

/** Почему пункт в «Уточнить» (FR-009). */
// 031 (FR-014; ASG-RES-10): person_unresolved — ответственный только нерешённый текст.
export const clarifyReasons = [
  'invalid_date',
  'due_text',
  'no_due',
  'no_responsible',
  'person_unresolved',
] as const;
export type ClarifyReason = (typeof clarifyReasons)[number];
export const ClarifyReasonSchema = z.enum(clarifyReasons);

export const clarifyReasonLabels = {
  invalid_date: 'невозможная дата',
  due_text: 'срок словами',
  no_due: 'нет срока',
  no_responsible: 'нет ответственного',
  person_unresolved: 'кто это — не решено',
} as const satisfies Record<ClarifyReason, string>;

/** Откуда взят ответственный (FR-008): строка, родительская строка, колонка «Подрядчик». */
export const responsibleSources = ['row', 'parent', 'contractor'] as const;
export type ResponsibleSource = (typeof responsibleSources)[number];
export const ResponsibleSourceSchema = z.enum(responsibleSources);

export const ParsedItemSchema = z.object({
  /** Номер строки листа, с 1 — по нему пункт находят в файле (FR-012). */
  row: z.number().int().min(1),
  kind: ItemKindSchema,
  /** Строка родителя у подпункта. */
  parentRow: z.number().int().min(1).nullable(),
  /** Путь раздела «верхний / объект» (research, Р4). */
  section: z.string(),
  /** Номер как в файле: «12», «7.1.»; у подпункта «–» номера может не быть. */
  number: z.string().nullable(),
  text: z.string(),
  /** Срок — последняя дата ячейки срока (FR-005). */
  due: DateSchema.nullable(),
  /** Текст ячейки срока, если это не одна чистая дата: «30.07 / 10.08 / 11.09.2026». */
  dueRaw: z.string().nullable(),
  /** Прежние даты ячейки срока по порядку — переносы (FR-005). */
  transfers: z.array(DateSchema),
  /**
   * «Закрыто». В срезе всегда false: цвет статуса не даёт (FR-006); свидетельство выполнения —
   * дата фактического окончания и текст, разобранный моделью, — следующий срез.
   */
  closed: z.boolean(),
  /** «Пауза» — слово паузы схемы шаблона у строки или у заголовка/пункта выше; от раздела — нет (FR-007). */
  paused: z.boolean(),
  /** Ответственный как записан, после подстановок; переносы строк — через «, ». */
  responsible: z.string().nullable(),
  responsibleFrom: ResponsibleSourceSchema.nullable(),
  /**
   * Текст колонки «Подрядчик» как в файле, если она есть в схеме шаблона и не пуста (005, FR-001):
   * справочник делит на упоминания и её, даже когда «Ответственный» заполнен.
   */
  contractor: z.string().nullable().optional(),
  clarify: ClarifyReasonSchema.nullable(),
  /** Текст рядом с датой в ячейке срока: «ПД», «до». */
  note: z.string().nullable(),
  /** Текст колонки примечания (комментария) как в файле — вход вопроса о свидетельстве (004). */
  comment: z.string().nullable().optional(),
  /**
   * Отметка выполнения (010, FR-046, FR-047): текст ячейки срока или примечания, где одно слово
   * из слов отметки схемы. Есть — пункт закрыт правилом (closed), вопрос 004 не задаётся.
   */
  mark: z.string().nullable().optional(),
  /** Решение ИИ о свидетельстве выполнения (004); нет — модель не спрашивали или отказ. */
  evidence: EvidenceSchema.nullable().optional(),
  /**
   * Структура строки по format-history-1.7 (010): группа или работа, скрытость, состояние
   * родителя и кандидаты, путь предков. Нет — пункт разобран до 0.5.0.
   */
  structure: ItemStructureSchema.optional(),
});
export type ParsedItem = z.infer<typeof ParsedItemSchema>;

/** Результат разбора одного листа (FR-011): одинаков при повторном разборе той же версией правил. */
export const ParsedSheetSchema = z.object({
  schemaId: z.string(),
  sheetName: z.string(),
  /** Дата листа (research, Р5); по ней по умолчанию считается дата отсчёта (FR-017). */
  sheetDate: DateSchema,
  rulesVersion: z.string(),
  items: z.array(ParsedItemSchema),
  /**
   * Полный снимок структуры листа (010, format-history-1.7): все содержательные строки, включая
   * скрытые, разделы и строки без названия, неопределённости и диагностику. Нет — разбор до 0.5.0.
   */
  structure: SheetStructureSchema.optional(),
  /** Прогон истории: листы книги по порядку, последний — этот лист. */
  structureRun: StructureRunSchema.optional(),
});
export type ParsedSheet = z.infer<typeof ParsedSheetSchema>;
